import { CloseOutlined } from '@ant-design/icons';
import { Button, Col, Row, Select, Skeleton, Space, theme, Typography } from 'antd';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { Controller, FieldArrayWithId, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import {
    DATE_TYPE_FIELD_TYPE_LIST,
    TWO_INPUT_REQUIRED_DATE_TIME_FIELD_OPERATOR_LIST,
    VALUE_REQUIRED_DATE_TIME_FIELD_OPERATOR_LIST,
    ValueRequiredDateTimeFieldOperatorType
} from '../../../../components/AdminPage/ConditionInput/ConditionDateTimeInput';
import { customFieldTypeMap } from '../../../../components/CustomField/CustomField.type';
import { CreateAdvancedReportFormValues } from '../../../../components/Forms/CreateAdvancedReportForm';
import { CreateAutomationFormValues } from '../../../../components/Forms/CreateAutomationForm';
import { CreateFilterFormValues } from '../../../../components/Forms/CreateFilterForm';
import { EditFilterFormValues } from '../../../../components/Forms/EditFilterForm';
import { AutoCompleteKeyMap } from '../../../../components/GrispiInput/components/GrispiAutoCompleteInput';
import GrispiInput, {
    grispiInputTypeMap,
    NOT_VALUE_REQUIRED_CONDITION_FIELD_TYPE
} from '../../../../components/GrispiInput/index';
import useCurrentFilter from '../../../../hooks/adminPage/filters/useCurrentFilter';
import { FilterMeta, FilterMetaCondition } from '../../../../hooks/adminPage/filters/useFilterMeta';
import { CustomFieldOption, FilterConditionAssigneeFieldKey, TicketSystemFieldsKey } from '../../../../model/types';
import useConditionOptions from '../../../../utils/useConditionOptions';
import FilterAllConditionFieldKeySelect from './FilterConditionFieldKeySelect';
import { ConditionSectionTypeMap } from './TicketConditionsContainer';

const { Text } = Typography;

const oldSchoolSelectTypeFieldKeys = ['channel', 'language', 'role', 'tm.scope'];

const FilterOperatorWithoutValueTypeMap = {
    PRESENT: 'PRESENT',
    NOT_PRESENT: 'NOT_PRESENT',
    YES: 'YES',
    NO: 'NO'
} as const;

export enum ConditionTypeMap {
    TICKET_ALL_CONDITIONS = 'TICKET_ALL_CONDITIONS',
    TICKET_ANY_CONDITIONS = 'TICKET_ANY_CONDITIONS',
    USER_ALL_CONDITIONS = 'USER_ALL_CONDITIONS',
    USER_ANY_CONDITIONS = 'USER_ANY_CONDITIONS'
}
interface Props {
    allConditions: FilterMeta['allConditions'];
    sectionType?: ConditionSectionTypeMap;
    isCreateMode?: boolean;
    conditionType?: ConditionTypeMap;
}

export const getFilterFieldConditionByFieldKey = <T extends FilterMetaCondition>(
    allConditions: T[],
    fieldKey: string
): T | undefined => allConditions.find((condition) => condition.key === fieldKey);

function isValueRequiredCondition(
    fieldItem: FieldArrayWithId<
        CreateFilterFormValues | EditFilterFormValues | CreateAutomationFormValues | CreateAdvancedReportFormValues,
        | `allConditions`
        | `filter.allConditions`
        | `anyConditions`
        | `filter.anyConditions`
        | `userAllConditions`
        | `filter.userAllConditions`
        | `userAnyConditions`
        | `filter.userAnyConditions`,
        'id'
    >
): boolean {
    return NOT_VALUE_REQUIRED_CONDITION_FIELD_TYPE.includes(fieldItem?.type as any)
        ? VALUE_REQUIRED_DATE_TIME_FIELD_OPERATOR_LIST.includes(fieldItem?.operator)
        : !Object.keys(FilterOperatorWithoutValueTypeMap).includes(fieldItem?.operator);
}

function refactorChannelOptions(fieldKey: string, options: CustomFieldOption[]) {
    if (fieldKey === 'channel') return options.map((option) => ({ ...option, value: option.label }));
    return options;
}

export function getFilterConditionFormFieldName(sectionType: Props['sectionType'], conditionType: ConditionTypeMap) {
    switch (conditionType) {
        case ConditionTypeMap.TICKET_ALL_CONDITIONS:
            return sectionType === ConditionSectionTypeMap.AUTOMATION ? 'filter.allConditions' : 'allConditions';
        case ConditionTypeMap.TICKET_ANY_CONDITIONS:
            return sectionType === ConditionSectionTypeMap.AUTOMATION ? 'filter.anyConditions' : 'anyConditions';
        case ConditionTypeMap.USER_ALL_CONDITIONS:
            return sectionType === ConditionSectionTypeMap.AUTOMATION
                ? 'filter.userAllConditions'
                : 'userAllConditions';
        case ConditionTypeMap.USER_ANY_CONDITIONS:
            return sectionType === ConditionSectionTypeMap.AUTOMATION
                ? 'filter.userAnyConditions'
                : 'userAnyConditions';
        default:
            return 'allConditions';
    }
}
export default function FilterAllConditions({
    allConditions = [],
    sectionType = ConditionSectionTypeMap.FILTER,
    isCreateMode,
    conditionType = ConditionTypeMap.TICKET_ALL_CONDITIONS
}: Props) {
    const {
        token: { colorPrimary }
    } = theme.useToken();
    const {
        query: { id }
    } = useRouter();
    const isReportPage = sectionType === ConditionSectionTypeMap.ADVANCED_REPORT;
    const isUserFilter = sectionType === ConditionSectionTypeMap.USER_FILTER;
    const isAutomation = sectionType === ConditionSectionTypeMap.AUTOMATION;
    const { data } = useCurrentFilter(sectionType === ConditionSectionTypeMap.FILTER ? Number(id) : null);
    const isPredefinedFilter =
        id && (sectionType === ConditionSectionTypeMap.FILTER ? data?.predefined : !!isAutomation);

    const { t } = useTranslation('admin');
    const {
        formState: { errors }
    } = useFormContext<
        CreateFilterFormValues | EditFilterFormValues | CreateAutomationFormValues | CreateAdvancedReportFormValues
    >();

    const { localizeOperators, localizeOptions, localizeCheckBoxOptions } = useConditionOptions();
    const needOppositeConditionForRules =
        sectionType !== ConditionSectionTypeMap.USER_FILTER
            ? conditionType === ConditionTypeMap.TICKET_ALL_CONDITIONS
            : conditionType === ConditionTypeMap.USER_ALL_CONDITIONS;
    const anyConditionsFormValue = useWatch<
        CreateFilterFormValues | EditFilterFormValues | CreateAutomationFormValues | CreateAdvancedReportFormValues,
        | `allConditions`
        | `filter.allConditions`
        | `anyConditions`
        | `filter.anyConditions`
        | `userAllConditions`
        | `filter.userAllConditions`
        | `userAnyConditions`
        | `filter.userAnyConditions`
    >({
        name: needOppositeConditionForRules
            ? getFilterConditionFormFieldName(sectionType, ConditionTypeMap.TICKET_ANY_CONDITIONS)
            : null
    });
    const allUserConditionsFormValue = useWatch<
        CreateFilterFormValues | EditFilterFormValues | CreateAutomationFormValues | CreateAdvancedReportFormValues,
        | `allConditions`
        | `filter.allConditions`
        | `anyConditions`
        | `filter.anyConditions`
        | `userAllConditions`
        | `filter.userAllConditions`
        | `userAnyConditions`
        | `filter.userAnyConditions`
    >({
        name: needOppositeConditionForRules
            ? getFilterConditionFormFieldName(sectionType, ConditionTypeMap.USER_ALL_CONDITIONS)
            : null
    });
    const anyUserConditionsFormValue = useWatch<
        CreateFilterFormValues | EditFilterFormValues | CreateAutomationFormValues | CreateAdvancedReportFormValues,
        | `allConditions`
        | `filter.allConditions`
        | `anyConditions`
        | `filter.anyConditions`
        | `userAllConditions`
        | `filter.userAllConditions`
        | `userAnyConditions`
        | `filter.userAnyConditions`
    >({
        name: needOppositeConditionForRules
            ? getFilterConditionFormFieldName(sectionType, ConditionTypeMap.USER_ANY_CONDITIONS)
            : null
    });

    const anyConditionsFormValueLength =
        anyConditionsFormValue?.length || allUserConditionsFormValue?.length || anyUserConditionsFormValue?.length || 0;

    const rules = useMemo(
        () =>
            needOppositeConditionForRules && !anyConditionsFormValueLength
                ? { required: t('Filters.errors.conditionError') }
                : { required: false },
        [anyConditionsFormValueLength, needOppositeConditionForRules, t]
    );

    const { fields, append, remove, update } = useFieldArray<
        CreateFilterFormValues | EditFilterFormValues | CreateAutomationFormValues | CreateAdvancedReportFormValues,
        | `allConditions`
        | `filter.allConditions`
        | `anyConditions`
        | `filter.anyConditions`
        | `userAllConditions`
        | `filter.userAllConditions`
        | `userAnyConditions`
        | `filter.userAnyConditions`
    >({
        name: getFilterConditionFormFieldName(sectionType, conditionType), // unique name for your Field Array
        rules
    });

    const errorMessage = !needOppositeConditionForRules
        ? false
        : sectionType === ConditionSectionTypeMap.AUTOMATION && 'filter' in errors
        ? errors?.filter?.allConditions?.root?.message
        : 'allConditions' in errors
        ? errors?.allConditions?.root?.message
        : 'userAllConditions' in errors
        ? errors?.userAllConditions?.root?.message
        : false;

    return (
        <Space size="small" direction="vertical" className="full-width">
            <Row>
                <Col span={24}>
                    <Typography.Text strong>
                        <Trans
                            i18nKey={`admin:Filters.${
                                conditionType.includes('ALL') ? 'AllConditionsTitle' : 'AnyConditionsTitle'
                            }${isReportPage ? 'Report' : isUserFilter ? 'User' : ''}`}
                            components={{ em: <em style={{ color: `${colorPrimary}` }} /> }}
                        />
                    </Typography.Text>
                </Col>
            </Row>
            {fields.map((fieldItem, index) => (
                <Col key={fieldItem.id}>
                    <Row gutter={8}>
                        <FilterAllConditionFieldKeySelect
                            index={index}
                            sectionType={sectionType}
                            isPredefinedFilter={isPredefinedFilter}
                            update={update}
                            isCreateMode={isCreateMode}
                            conditions={allConditions}
                            conditionType={conditionType}
                        />
                        <Col span={7}>
                            <Controller<
                                | CreateFilterFormValues
                                | EditFilterFormValues
                                | CreateAutomationFormValues
                                | CreateAdvancedReportFormValues,
                                | `allConditions.${number}.operator`
                                | `filter.allConditions.${number}.operator`
                                | `anyConditions.${number}.operator`
                                | `filter.anyConditions.${number}.operator`
                                | `userAllConditions.${number}.operator`
                                | `filter.userAllConditions.${number}.operator`
                                | `userAnyConditions.${number}.operator`
                                | `filter.userAnyConditions.${number}.operator`
                            >
                                name={`${getFilterConditionFormFieldName(
                                    sectionType,
                                    conditionType
                                )}.${index}.operator`}
                                render={({ field }) => {
                                    const fieldCondition = getFilterFieldConditionByFieldKey(
                                        allConditions,
                                        fieldItem.fieldKey
                                    );

                                    if (!fieldCondition) return null;
                                    const operatorOptions = fieldCondition?.operators?.map((operator) => ({
                                        label: operator,
                                        value: operator
                                    }));

                                    return (
                                        <Select
                                            disabled={
                                                isPredefinedFilter ||
                                                fieldItem.fieldKey === TicketSystemFieldsKey.assignee
                                            }
                                            className="full-width"
                                            options={localizeOperators(operatorOptions)}
                                            placeholder={t('Filters.operatorPlaceholder')}
                                            {...field}
                                            onChange={(value: string) => {
                                                update(index, {
                                                    ...fieldItem,
                                                    operator: value,
                                                    value: TWO_INPUT_REQUIRED_DATE_TIME_FIELD_OPERATOR_LIST.includes(
                                                        value as any
                                                    )
                                                        ? '1800000:min'
                                                        : VALUE_REQUIRED_DATE_TIME_FIELD_OPERATOR_LIST.includes(value)
                                                        ? null
                                                        : null
                                                });
                                            }}
                                        />
                                    );
                                }}
                            />
                        </Col>

                        <Col span={7}>
                            {isValueRequiredCondition(fieldItem) && (
                                <Controller<
                                    | CreateFilterFormValues
                                    | EditFilterFormValues
                                    | CreateAutomationFormValues
                                    | CreateAdvancedReportFormValues,
                                    | `allConditions.${number}.value`
                                    | `filter.allConditions.${number}.value`
                                    | `anyConditions.${number}.value`
                                    | `filter.anyConditions.${number}.value`
                                    | `userAllConditions.${number}.value`
                                    | `filter.userAllConditions.${number}.value`
                                    | `userAnyConditions.${number}.value`
                                    | `filter.userAnyConditions.${number}.value`
                                >
                                    name={`${getFilterConditionFormFieldName(
                                        sectionType,
                                        conditionType
                                    )}.${index}.value`}
                                    rules={{
                                        validate: (value) => {
                                            if (
                                                value &&
                                                TWO_INPUT_REQUIRED_DATE_TIME_FIELD_OPERATOR_LIST.includes(
                                                    fieldItem.operator as any
                                                )
                                            ) {
                                                const parsedValue = value.toString().split(':')[0];

                                                if (Number.isNaN(+parsedValue)) return t('Filters.errors.valueError');

                                                return (
                                                    +parsedValue <= 60 * 60 * 1000 * 24 * 365 ||
                                                    t('Filters.errors.durationError')
                                                );
                                            }
                                            return !!value || t('Filters.errors.valueError');
                                        }
                                    }}
                                    render={({ field: { ref, ...rest }, fieldState: { error } }) => {
                                        const fieldCondition = getFilterFieldConditionByFieldKey(
                                            allConditions,
                                            fieldItem.fieldKey
                                        );
                                        if (!fieldCondition) return <Skeleton.Input active className="full-width" />;
                                        const inputType =
                                            fieldCondition.type === customFieldTypeMap.CHECKBOX
                                                ? customFieldTypeMap.SELECT
                                                : fieldCondition.key === FilterConditionAssigneeFieldKey.group ||
                                                  fieldCondition.key === AutoCompleteKeyMap.organization
                                                ? grispiInputTypeMap.AUTOCOMPLETE
                                                : fieldCondition.type;
                                        const autocompleteKey =
                                            fieldCondition.key === FilterConditionAssigneeFieldKey.group
                                                ? AutoCompleteKeyMap.group
                                                : AutoCompleteKeyMap.organization;
                                        return (
                                            <GrispiInput
                                                disabled={
                                                    isPredefinedFilter ||
                                                    fieldItem.fieldKey === TicketSystemFieldsKey.assignee
                                                }
                                                type={inputType}
                                                conditionInput
                                                containerProps={{ noLabel: true, errorMessage: error?.message }}
                                                {...rest}
                                                onChange={(value: string | number) => {
                                                    rest.onChange(value);
                                                }}
                                                antdProps={{
                                                    allowClear: false,
                                                    ...(DATE_TYPE_FIELD_TYPE_LIST.includes(fieldCondition.type) &&
                                                    VALUE_REQUIRED_DATE_TIME_FIELD_OPERATOR_LIST.includes(
                                                        fieldItem.operator
                                                    )
                                                        ? {}
                                                        : {
                                                              placeholder:
                                                                  fieldCondition.options?.length > 0
                                                                      ? t('Filters.placeholders.selectPlaceholder')
                                                                      : t('Filters.placeholders.inputPlaceholder')
                                                          })
                                                }}
                                                {...(VALUE_REQUIRED_DATE_TIME_FIELD_OPERATOR_LIST.includes(
                                                    fieldItem.operator
                                                )
                                                    ? {
                                                          operator:
                                                              fieldItem.operator as ValueRequiredDateTimeFieldOperatorType
                                                      }
                                                    : {})}
                                                options={
                                                    fieldCondition.type === customFieldTypeMap.CHECKBOX
                                                        ? localizeCheckBoxOptions(fieldCondition.options as any)
                                                        : localizeOptions(
                                                              fieldCondition.key as any,
                                                              refactorChannelOptions(
                                                                  fieldCondition.key,
                                                                  fieldCondition.options
                                                              )
                                                          )
                                                }
                                                {...(fieldCondition.options?.length > 0 ? { ref } : {})} // TODO: cagri, forward this ref
                                                {...(fieldCondition.key === TicketSystemFieldsKey.followers ||
                                                fieldCondition.key === FilterConditionAssigneeFieldKey.user
                                                    ? { isAgentInput: true }
                                                    : {})}
                                                {...(inputType === grispiInputTypeMap.AUTOCOMPLETE
                                                    ? { autocompleteKey }
                                                    : {})}
                                                fieldKey={fieldCondition.key}
                                            />
                                        );
                                    }}
                                />
                            )}
                        </Col>
                        <Col span={3}>
                            <Button
                                disabled={isPredefinedFilter}
                                type={'text'}
                                icon={<CloseOutlined />}
                                onClick={() => {
                                    remove(index);
                                }}
                            />
                        </Col>
                    </Row>
                </Col>
            ))}

            <Col span={24}>
                <Button
                    disabled={isPredefinedFilter}
                    loading={!allConditions}
                    onClick={() => {
                        append(
                            {
                                fieldKey: allConditions[0]?.key,
                                operator: allConditions[0]?.operators?.[0],
                                value: null,
                                type: allConditions[0]?.type
                            },
                            { shouldFocus: true }
                        );
                    }}
                >
                    {t('newFilter.addCondition')}
                </Button>
            </Col>
            {errorMessage && <Text type="danger">{errorMessage}</Text>}
        </Space>
    );
}

export { isValueRequiredCondition, oldSchoolSelectTypeFieldKeys, refactorChannelOptions };
